import { Button } from '@/components/ui/button';
import { MobileCarousel } from '@/components/MobileCarousel';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, ShoppingBag, Shield, Award, Star, TrendingUp } from 'lucide-react';

export default function AmazonBrandRegistryPage({ onCTAClick }) {
  return (
    <div>
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.pexels.com/photos/4560039/pexels-photo-4560039.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-accent mb-4">Amazon Brand Registry</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-primary mb-6 leading-tight">
              Enroll in Amazon Brand Registry & Protect Your Products
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
              Unlock powerful brand protection and marketing tools on Amazon with Brand Registry enrollment. We help you register your brand on Amazon, protect your listings from hijackers, and access enhanced marketing features to grow your e-commerce business.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white" onClick={onCTAClick}>
              Enroll in Brand Registry
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
                Amazon Brand Registry Services for Sellers
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Amazon Brand Registry is a program that provides brand owners with enhanced control over their product listings on Amazon. To enroll in Brand Registry Amazon, you need an active registered trademark from USPTO or other eligible trademark offices. Once enrolled, you gain access to powerful tools for protecting your brand, managing product content, and accessing advanced marketing features like A+ Content, Sponsored Brands ads, and Amazon Stores.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                Our Amazon Brand Registry services include trademark registration assistance, Brand Registry enrollment application, verification support, account setup and configuration, and training on Brand Registry features. Whether you are a new Amazon seller launching your brand or an established seller looking to protect your listings from counterfeiters and hijackers, we provide the expert guidance you need to successfully register your brand on Amazon and leverage its powerful brand protection tools.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <img src="https://images.unsplash.com/photo-1632813405743-ab79b1d3d98b" alt="Amazon seller dashboard" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Benefits of Amazon Brand Registry
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Brand Protection', desc: 'Powerful tools to detect and report potential trademark infringements, counterfeit products, and unauthorized sellers hijacking your listings.' },
              { icon: Award, title: 'Content Control', desc: 'Greater control over your product detail pages with the ability to update content, images, and descriptions directly.' },
              { icon: TrendingUp, title: 'Enhanced Marketing', desc: 'Access to A+ Content, Sponsored Brands ads, Amazon Stores, and other premium marketing features to boost sales.' },
              { icon: Check, title: 'Listing Accuracy', desc: 'Ensure your product information remains accurate with priority in search results and influence over product detail pages.' },
              { icon: ShoppingBag, title: 'Counterfeit Prevention', desc: 'Proactive brand protection with automated monitoring and streamlined reporting of intellectual property violations.' },
              { icon: Shield, title: 'Global Protection', desc: 'Unified brand registry account works across all Amazon marketplaces where you sell, providing consistent protection worldwide.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <item.icon className="h-12 w-12 text-accent mb-6" />
                <h3 className="text-xl font-bold text-primary mb-4">{item.title}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              What Our Amazon Brand Registry Services Include
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: 'Trademark Registration Assistance', desc: 'If you don\'t have a registered trademark yet, we handle your USPTO trademark registration, which is required for Brand Registry enrollment.' },
              { title: 'Eligibility Assessment', desc: 'We review your trademark and Amazon account to ensure you meet all Brand Registry requirements before starting the enrollment process.' },
              { title: 'Brand Registry Application', desc: 'Our team completes your Brand Registry enrollment application with accurate trademark information, product images, and brand verification details.' },
              { title: 'Verification Support', desc: 'We assist with Amazon\'s brand verification process, including responding to verification codes and providing additional documentation if needed.' },
              { title: 'Account Configuration', desc: 'We help set up your Brand Registry account, add authorized users, configure brand protection settings, and enable key features.' },
              { title: 'Feature Training', desc: 'We provide guidance on using Brand Registry tools including A+ Content creation, brand protection reporting, and enhanced listing management.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                  <Check className="h-6 w-6 text-accent" />
                  {item.title}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Simple 3-Step Brand Registry Enrollment
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            {['Get Your Trademark Registered', 'We Complete Your Enrollment', 'Access Brand Registry Features'].map((title, i) => (
              <div key={i} className="bg-white border border-border p-8 relative">
                <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">{i+1}</div>
                <div className="mt-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">{title}</h3>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    {[
                      'If you don\'t have a registered trademark, we handle your USPTO trademark registration first, which typically takes 8-12 months.',
                      'Once your trademark is registered, we complete your Brand Registry application, provide verification support, and configure your account.',
                      'Start using powerful brand protection tools, enhanced content features, and premium marketing capabilities on Amazon.'
                    ][i]}
                  </p>
                </div>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            {[1,2,3].map((i) => (
              <div key={i} className="bg-white border border-border p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-base leading-relaxed text-muted-foreground mb-6">
                  "Get Logo Trademarked helped us register our brand on Amazon Brand Registry. The enrollment process was smooth and now we have full control over our product listings!"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                    {['CW', 'MG', 'DK'][i-1]}
                  </div>
                  <div>
                    <div className="font-semibold text-primary">{['Chris Wilson', 'Maria Garcia', 'Daniel Kim'][i-1]}</div>
                    <div className="text-sm text-muted-foreground">Amazon Seller</div>
                  </div>
                </div>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Amazon Brand Registry FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'What do I need to enroll in Amazon Brand Registry?', a: 'You need an active registered trademark from USPTO or other eligible trademark office, an Amazon seller account, and product listings that match your registered trademark. The trademark must be text-based or image-based (logos) and actively used in commerce.' },
              { q: 'How long does Brand Registry enrollment take?', a: 'Once you have a registered trademark, Brand Registry enrollment typically takes 1-3 business days after application submission. Amazon reviews your application and may request additional verification before approval.' },
              { q: 'Can I use a pending trademark for Brand Registry?', a: 'Amazon Brand Registry requires a registered trademark that has been approved by USPTO or other eligible trademark offices. Pending trademark applications do not qualify for enrollment.' },
              { q: 'Does Brand Registry protect my brand globally?', a: 'Brand Registry protects your brand across all Amazon marketplaces where you enroll. A single Brand Registry account can cover multiple marketplaces, but you may need separate trademark registrations for each country.' }
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i+1}`} className="bg-white border border-border px-8">
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Protect Your Amazon Brand?
          </h2>
          <p className="text-lg leading-relaxed text-white/90 mb-8">
            Enroll in Amazon Brand Registry and unlock powerful brand protection tools. Get started today!
          </p>
          <Button size="lg" onClick={onCTAClick} className="bg-accent hover:bg-accent/90 text-primary">
            Start Brand Registry Enrollment
          </Button>
        </div>
      </section>
    </div>
  );
}
